import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Badge,
  Spinner,
} from "react-bootstrap";
import {
  DatatableWrapper,
  TableHeader,
  TableBody,
  Pagination,
  PaginationOptions,
  Filter,
} from "react-bs-datatable";
import { FaSearch, FaFilePdf } from "react-icons/fa";
import reportService from "../../services/reportService";
import categoryService from "../../services/categoryService";
import subCategoryService from "../../services/subCategoryService";
import { productService } from "../../services/productService";
import InfoPill from "../Common/InfoPill";
import "./Report.css";
import { formatQuantity, formatQuantityWithUnit, formatDetailedQuantity } from "../../utils/quantityUtils";
import { Link } from "react-router-dom";

const CancelledOrdersReport = () => {
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [products, setProducts] = useState([]);

  // Filters
  const [filters, setFilters] = useState({
    category_id: "",
    sub_category_id: "",
    product_id: "",
    start_date: "",
    end_date: "",
  });

  // Ref to track latest filters for immediate access
  const filtersRef = useRef(filters);

  // Initialize ref with current filters whenever filters change
  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  useEffect(() => {
    loadFilterOptions();
  }, []);

  useEffect(() => {
    if (filters.category_id) {
      loadSubCategoriesByCategory(filters.category_id);
    } else {
      setSubCategories([]);
    }
  }, [filters.category_id]);

  useEffect(() => {
    loadReportData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadFilterOptions = async () => {
    try {
      const [catsRes, prodsRes] = await Promise.all([
        categoryService.getAllCategories({ status: "Active" }),
        productService.getProducts({ limit: 1000 }),
      ]);

      setCategories(catsRes?.categories || catsRes || []);
      setProducts(prodsRes?.products || prodsRes || []);
    } catch (error) {
      console.error("Error loading filter options:", error);
    }
  };

  const loadSubCategoriesByCategory = async (categoryId) => {
    try {
      const response = await subCategoryService.getSubCategoriesByCategoryId(
        categoryId
      );
      const subCats =
        response?.subCategories ||
        response?.data?.subCategories ||
        response?.data ||
        response ||
        [];
      setSubCategories(Array.isArray(subCats) ? subCats : []);
    } catch (error) {
      console.error("Error loading subcategories:", error);
      setSubCategories([]);
    }
  };

  const loadReportData = async (filterParams = null) => {
    try {
      setLoading(true);
      // Use provided filters or fall back to state
      const filtersToUse = filterParams !== null ? filterParams : filters;

      const cleanedFilters = {};
      if (
        filtersToUse.category_id !== undefined &&
        filtersToUse.category_id !== null &&
        filtersToUse.category_id !== ""
      ) {
        cleanedFilters.category_id = String(filtersToUse.category_id).trim();
      }
      if (
        filtersToUse.sub_category_id !== undefined &&
        filtersToUse.sub_category_id !== null &&
        filtersToUse.sub_category_id !== ""
      ) {
        cleanedFilters.sub_category_id = String(
          filtersToUse.sub_category_id
        ).trim();
      }
      if (
        filtersToUse.product_id !== undefined &&
        filtersToUse.product_id !== null &&
        filtersToUse.product_id !== ""
      ) {
        cleanedFilters.product_id = String(filtersToUse.product_id).trim();
      }
      if (filtersToUse.start_date && filtersToUse.start_date !== "") {
        cleanedFilters.start_date = filtersToUse.start_date;
      }
      if (filtersToUse.end_date && filtersToUse.end_date !== "") {
        // Set end_date to end of day (23:59:59) to include the entire day
        // This ensures that when start_date and end_date are the same, we get all records for that day
        cleanedFilters.end_date = `${filtersToUse.end_date} 23:59:59`;
      }

      const response = await reportService.getCancelledOrdersReport(
        cleanedFilters
      );

      // Handle different response structures
      let data = [];
      if (response) {
        if (response.orders && Array.isArray(response.orders)) {
          data = response.orders;
        } else if (response.data && Array.isArray(response.data)) {
          data = response.data;
        } else if (Array.isArray(response)) {
          data = response;
        }
      }

      const dataWithSrNo = Array.isArray(data)
        ? data.map((item, index) => ({
            ...item,
            sr_no: index + 1,
          }))
        : [];
      setReportData(dataWithSrNo);
    } catch (error) {
      console.error("Error loading cancelled orders report:", error);
      setReportData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => {
      const updatedFilters = {
        ...prev,
        [field]: value,
        ...(field === "category_id" && { sub_category_id: "", product_id: "" }),
        ...(field === "sub_category_id" && { product_id: "" }),
      };
      // Update ref immediately
      filtersRef.current = updatedFilters;
      return updatedFilters;
    });
  };

  const handleSearch = () => {
    // Get the latest filters from ref (always up to date)
    const currentFilters = filtersRef.current;
    // Load report data with current filters
    loadReportData(currentFilters);
  };

  const handleReset = () => {
    const resetFilters = {
      category_id: "",
      sub_category_id: "",
      product_id: "",
      start_date: "",
      end_date: "",
    };
    setFilters(resetFilters);
    // Update ref immediately
    filtersRef.current = resetFilters;
    // Load data with reset filters
    loadReportData(resetFilters);
  };

  const handleDownloadPDF = async () => {
    try {
      setDownloading(true);
      const cleanedFilters = {};
      if (filters.category_id) cleanedFilters.category_id = filters.category_id;
      if (filters.sub_category_id)
        cleanedFilters.sub_category_id = filters.sub_category_id;
      if (filters.product_id) cleanedFilters.product_id = filters.product_id;
      if (filters.start_date) cleanedFilters.start_date = filters.start_date;
      if (filters.end_date) {
        // Set end_date to end of day (23:59:59) to include the entire day
        // This ensures that when start_date and end_date are the same, we get all records for that day
        cleanedFilters.end_date = `${filters.end_date} 23:59:59`;
      }

      const blob = await reportService.downloadReportPDF(
        "cancelled-orders",
        cleanedFilters
      );
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `cancelled-orders-report-${
        new Date().toISOString().split("T")[0]
      }.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error("Error downloading PDF:", error);
      alert("Failed to download PDF. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  // Helper function to format date as dd-mm-yyyy with 12hr time
  const formatDate = (dateString) => {
    const { formatDateTimeToDDMMYYYY12Hr } = require("../../utils/dateUtils");
    return formatDateTimeToDDMMYYYY12Hr(dateString) || "-";
  };

  // Helper function to convert text to title case
  const toTitleCase = (str) => {
    if (!str) return "";
    return str
      .toLowerCase()
      .split(/[\s_-]+/)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  // Helper function to get unit for a product
  const getProductUnit = (item) => {
    // Try to find product by product_id first
    if (item.product_id) {
      const product = products.find(
        (p) => String(p.id) === String(item.product_id)
      );
      if (product && product.unit) {
        return product.unit;
      }
    }
    // Fallback: try to find by product_name
    if (item.product_name) {
      const product = products.find(
        (p) =>
          p.name && p.name.toLowerCase() === item.product_name.toLowerCase()
      );
      if (product && product.unit) {
        return product.unit;
      }
    }
    // Default unit if not found
    return "piece";
  };

  const getStatusBadge = (status) => {
    const s = (status || "").toLowerCase();
    return s === "cancelled" ? "danger" : "secondary";
  };

  const headers = useMemo(() => [
    {
      prop: "sr_no",
      title: "Sr. No.",
      isSortable: true,
      cell: (row) => row.sr_no || "-",
    },
    {
      prop: "order_number",
      title: "Order Number",
      isSortable: true,
      isFilterable: true,
      cell: (row) => row.order_no || (row.id ? `#${row.id.slice(-8)}` : "-"),
    },
    {
      prop: "created_at",
      title: "Order Date",
      isSortable: true,
      cell: (row) => formatDate(row.created_at),
    },
    {
      prop: "customer_name",
      title: "Customer Name",
      isSortable: true,
      isFilterable: true,
      cell: (row) => {
        const name = row.customer_name || "-";
        const phone = row.customer_phone || "-";
        return (
          <div>
            <div className="fw-semibold">{name}</div>
            <small className="text-muted">{phone}</small>
          </div>
        );
      },
    },
    {
      prop: "categories",
      title: "Categories",
      isSortable: true,
      isFilterable: true,
      cell: (row) => row.categories || "-",
    },
    {
      prop: "sub_categories",
      title: "Sub Categories",
      isSortable: true,
      cell: (row) => row.sub_categories || "-",
    },
    {
      prop: "total_amount",
      title: "Total Amount",
      isSortable: true,
      cell: (row) => (
        <span className="fw-semibold">
          {parseFloat(row.total_amount || 0).toFixed(2)}
        </span>
      ),
    },
    {
      prop: "items_count",
      title: "Items Count",
      cell: (row) =>
        row.items_count || row.items?.length || row.order_items?.length || 0,
    },
    {
      prop: "items",
      title: "Order Items",
      cell: (row) => {
        const items = row.items || [];
        if (!items || items.length === 0) {
          return <span className="text-muted">No items</span>;
        }
        return (
          <div style={{ maxWidth: "450px", minWidth: "300px" }}>
            {items.map((item, index) => (
              <div
                key={index}
                className="mb-1 p-2 border rounded bg-white"
                style={{ fontSize: "0.875rem" }}
              >
                <div className="d-flex justify-content-between align-items-center">
                  <div className="flex-grow-1 me-2">
                    {/* Product Name */}
                    <div
                      className="fw-semibold text-truncate"
                      title={item.product_name || "Unknown Product"}
                    >
                      {item.product_name || "Unknown Product"}
                    </div>
                    <small className="text-muted">
                      {(() => {
                        // Get unit from item (now fetched from backend) or fallback to getProductUnit
                        const unit = item.unit || getProductUnit(item);
                        const hasDetailedQuantity = item.order_quantity && item.product_quantity;
                        
                        if (hasDetailedQuantity) {
                          return (
                            <>
                              Qty: <strong>{formatQuantity(item.order_quantity, unit)} X {formatQuantity(item.product_quantity, unit)} {unit}</strong>
                              <br />
                              Price: ₹{parseFloat(item.price || 0).toFixed(2)} per {formatQuantity(item.product_quantity, unit)} {unit}
                            </>
                          );
                        } else {
                          return (
                            <>
                              Qty: <strong>{formatQuantityWithUnit(item.quantity, unit)}</strong>
                              <br />
                              Price: ₹{parseFloat(item.price || 0).toFixed(2)}
                            </>
                          );
                        }
                      })()}
                    </small>
                  </div>
                  {/* Total Price on Right Side */}
                  <div className="text-end">
                    <div className="fw-bold">
                      ₹{parseFloat(item.subtotal || 0).toFixed(2)}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        );
      },
    },
    {
      prop: "discount_amount",
      title: "Discount Amount",
      isSortable: true,
      cell: (row) => {
        const discount = parseFloat(row.discount_amount || 0);
        return discount > 0 ? (
          <span className="">{discount.toFixed(2)}</span>
        ) : (
          <span className="text-muted">₹0.00</span>
        );
      },
    },
    {
      prop: "tax_amount",
      title: "Tax Amount",
      isSortable: true,
      cell: (row) => {
        const totalAmount = parseFloat(row.total_amount || 0);
        const discountAmount = parseFloat(row.discount_amount || 0);
        const taxPercentage = parseFloat(row.tax_percentage || 0);
        const taxAmount =
          ((totalAmount - discountAmount) * taxPercentage) / 100;
        return (
          <span>
            {taxAmount.toFixed(2)}
            {taxPercentage > 0 && (
              <small className="text-muted ms-1">({taxPercentage}%)</small>
            )}
          </span>
        );
      },
    },
    {
      prop: "delivery_charge",
      title: "Delivery Charge",
      isSortable: true,
      cell: (row) => (
        <span>{parseFloat(row.delivery_charge || 0).toFixed(2)}</span>
      ),
    },
    {
      prop: "final_amount",
      title: "Net Payable Amount",
      isSortable: true,
      cell: (row) => (
        <span className="fw-bold">
          {parseFloat(row.final_amount || 0).toFixed(2)}
        </span>
      ),
    },
    {
      prop: "order_status",
      title: "Status",
      isSortable: true,
      cell: (row) => (
        <Badge bg={getStatusBadge(row.order_status)}>
          {toTitleCase(row.order_status || "cancelled")}
        </Badge>
      ),
    },
  ], []);

  const labels = useMemo(
    () => ({ beforeSelect: " " }),
    []
  );

  // Filter products displayed in Product select by selected Category/Subcategory
  const filteredProducts = products.filter((p) => {
    if (filters.category_id) {
      const productCategoryId =
        p.category_id != null ? String(p.category_id) : null;
      if (productCategoryId !== String(filters.category_id)) return false;
    }
    if (filters.sub_category_id) {
      const productSubCategoryId =
        p.sub_category_id != null ? String(p.sub_category_id) : null;
      if (productSubCategoryId !== String(filters.sub_category_id))
        return false;
    }
    return true;
  });

  return (
    <Container fluid className="report-container">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <Link className="breadcrum" to="/">
          Home{"/"}
          <i
            className="fa-solid fa-chevron-right"
            style={{ marginLeft: "1px", marginRight: "1px" }}
          ></i>{" "}
          Reports / Cancelled Orders Report
        </Link>
      </div>

      {/* Filters Section */}
      <Card className="filter-card">
        <Card.Header className="filter-header">
          <h5>Filters</h5>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Category</Form.Label>
                <Form.Select
                  value={filters.category_id}
                  onChange={(e) =>
                    handleFilterChange("category_id", e.target.value)
                  }
                >
                  <option value="">All</option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Sub Category</Form.Label>
                <Form.Select
                  value={filters.sub_category_id}
                  onChange={(e) =>
                    handleFilterChange("sub_category_id", e.target.value)
                  }
                  disabled={!filters.category_id}
                >
                  <option value="">All</option>
                  {subCategories.map((sub) => (
                    <option key={sub.id} value={sub.id}>
                      {sub.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Product</Form.Label>
                <Form.Select
                  value={filters.product_id}
                  onChange={(e) =>
                    handleFilterChange("product_id", e.target.value)
                  }
                >
                  <option value="">All</option>
                  {filteredProducts.length > 0 ? (
                    filteredProducts.map((prod) => (
                      <option key={prod.id} value={prod.id}>
                        {prod.name}
                      </option>
                    ))
                  ) : (
                    <option disabled>No products available</option>
                  )}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Start Date</Form.Label>
                <Form.Control
                  type="date"
                  value={filters.start_date}
                  onChange={(e) =>
                    handleFilterChange("start_date", e.target.value)
                  }
                />
              </Form.Group>
            </Col>
            <Col md={3} className="mt-3">
              <Form.Group>
                <Form.Label>End Date</Form.Label>
                <Form.Control
                  type="date"
                  value={filters.end_date}
                  onChange={(e) =>
                    handleFilterChange("end_date", e.target.value)
                  }
                />
              </Form.Group>
            </Col>
            <Col md={3} className="d-flex align-items-end">
              <Button
                variant="primary"
                className="report-btn-search"
                onClick={handleSearch}
              >
                <FaSearch /> Search
              </Button>
              <Button
                variant="outline-secondary"
                className="ms-2"
                onClick={handleReset}
              >
                Reset
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Report Content */}
      <Card className="report-content-card">
        <Card.Body>
          {reportData.length === 0 && loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : (
            <DatatableWrapper
              body={reportData}
              headers={headers}
              paginationOptionsProps={{
                initialState: { rowsPerPage: 10, options: [10, 25, 50, 100] },
              }}
            >
              <div className="compact-controls d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
                <div className="d-flex align-items-center">
                  <div
                    className="flex-grow-1 me-2"
                    style={{ maxWidth: "300px" }}
                  >
                    <Filter
                      placeholder="Search cancelled orders..."
                      className="w-100"
                    />
                  </div>
                  <PaginationOptions labels={labels} />
                  <InfoPill left="Total Orders" right={reportData.length} />
                </div>

                <div>
                  <Button
                    variant="danger"
                    className="report-btn-download"
                    onClick={handleDownloadPDF}
                    disabled={downloading || loading || reportData.length === 0}
                  >
                    {downloading ? (
                      <>
                        <Spinner
                          animation="border"
                          size="sm"
                          className="me-2"
                        />
                        Downloading...
                      </>
                    ) : (
                      <>
                        <FaFilePdf /> Download PDF
                      </>
                    )}
                  </Button>
                </div>
              </div>

              <div
                className="table-container shadow-sm scroll-table"
                style={{ maxWidth: "1200px", position: "relative" }}
              >
                {loading && (
                  <div
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: "rgba(255, 255, 255, 0.8)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      zIndex: 10,
                      borderRadius: "0.375rem",
                    }}
                  >
                    <Spinner animation="border" variant="primary" />
                  </div>
                )}
                <table
                  className="table table-striped table-bordered align-middle"
                  style={{ marginBottom: "0px" }}
                >
                  <TableHeader />
                  <TableBody />
                </table>
              </div>

              <div className="compact-pagination d-flex justify-content-left mt-3">
                <Pagination classes={{
                  button: "bg-dark",
                }} />
              </div>
            </DatatableWrapper>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
};

export default CancelledOrdersReport;
